'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';

const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per chunk
const STORAGE_KEY = 'upload-state';
const STORAGE_EXPIRY_HOURS = 48; // 48 hours expiration
const SPEED_SAMPLE_SIZE = 10; // Number of speed samples to keep
const SPEED_SAMPLE_INTERVAL = 250; // Minimum time between samples in ms

export type UploadFile = {
  id: string;
  file: File;
  fileName: string;
  fileSize: number;
  fileType: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error' | 'paused';
  uploadId?: string;
  parts: { PartNumber: number; ETag: string }[];
  error?: string;
  currentChunk: number;
  totalChunks: number;
  chunkProgress: number;
  lastUpdated: number;
  uploadSpeed: number; // Speed in bytes per second
  uploadStartTime?: number; // When the current chunk upload started
  eta?: number; // Estimated time remaining in seconds
  speedSamples: number[]; // Array to store speed samples
  uploadStartedAt?: number; // Timestamp when upload started
  accountId: string;
  key: string;
  bucketName: string;
  uploadKey?: string;
};

type PersistedUploadState = Omit<UploadFile, 'file'> & {
  resumable: boolean;
  expiresAt: number; // Timestamp when this stored data expires
  speedSamples: number[];
  uploadStartedAt?: number;
};

export type UploadFileResult = {
  id: string;
  name: string;
  size: number;
  key: string;
  bucketName: string;
};

/**
 * Calculates the moving average speed from a collection of speed samples
 */
const calculateMovingAverage = (samples: number[]): number => {
  if (samples.length === 0) return 0;
  return samples.reduce((a, b) => a + b, 0) / samples.length;
};

type FileUploaderContextType = {
  /**
   * Current list of files being uploaded
   */
  files: UploadFile[];

  /**
   * List of paused uploads that can be resumed
   */
  pausedUploads: PersistedUploadState[];

  /**
   * Reference to the file input element
   */
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  /**
   * Upload all pending files
   */
  uploadAllPending: (onComplete?: (results: UploadFileResult) => void) => void;

  /**
   * Start all pending files
   */
  startUpload: ({
    filesToUpload,
    onComplete,
  }: {
    filesToUpload: UploadFile[];
    onComplete?: (results: UploadFileResult) => void;
  }) => void;

  /**
   * Handle file selection and initiate uploads
   */
  handleFileSelect: (
    files: {
      file: File;
      key: string;
      bucketName: string;
    }[],
    params: {
      uploadKey?: string;
      accountId: string;
    },
    onComplete?: (results: UploadFileResult[]) => void,
  ) => Promise<void>;

  /**
   * Upload a specific file
   */
  uploadFile: (
    fileData: UploadFile,
    onComplete?: (results: UploadFileResult) => void,
  ) => Promise<{
    id: string;
    name: string;
    size: number;
  }>;

  /**
   * Remove a file from the upload queue
   */
  removeFile: (id: string) => void;

  /**
   * Resume a paused upload
   */
  resumeUpload: (
    fileData: UploadFile,
    onComplete?: (results: UploadFileResult) => void,
  ) => void;

  /**
   * Whether the upload manager UI is open
   */
  isOpen: boolean;

  /**
   * Set whether the upload manager UI is open
   */
  setIsOpen: (isOpen: boolean) => void;
};

const FileUploaderContext = createContext<FileUploaderContextType | null>(null);

/**
 * Hook to access the file uploader context
 * @returns The file uploader context
 */
export const useFileUploader = () => {
  const context = useContext(FileUploaderContext);
  if (!context) {
    throw new Error('useFileUploader must be used within FileUploaderProvider');
  }
  return context;
};

/**
 * Provider component for the file uploader context
 * Manages uploads state and provides methods for handling file uploads
 */
export const FileUploaderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [pausedUploads, setPausedUploads] = useState<PersistedUploadState[]>(
    [],
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    try {
      const savedUploads = localStorage.getItem(STORAGE_KEY);
      if (savedUploads) {
        const parsedUploads: PersistedUploadState[] = JSON.parse(savedUploads);

        // Filter out completed, expired or very old uploads
        const now = Date.now();
        const validUploads = parsedUploads.filter(
          (upload) =>
            upload.status !== 'completed' &&
            upload.resumable &&
            upload.expiresAt > now,
        );

        // Store valid paused uploads for later matching
        setPausedUploads(validUploads);

        // Convert to UploadFile array with placeholder File objects
        // Real files will need to be re-selected by the user
        setFiles(
          validUploads.map((upload) => ({
            ...upload,
            file: new File([], upload.fileName, { type: upload.fileType }),
            status: 'paused',
            uploadSpeed: 0,
            speedSamples: [], // Initialize empty array for speed samples
          })),
        );
      }
    } catch (error) {
      console.error('Error loading saved uploads:', error);
    }
  }, []);

  // Cleanup expired items in localStorage
  useEffect(() => {
    const cleanupStorage = () => {
      try {
        // Get all keys in localStorage that match our pattern
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(STORAGE_KEY)) {
            keys.push(key);
          }
        }

        // Check each key for expired items
        const now = Date.now();
        keys.forEach((key) => {
          const data = localStorage.getItem(key);
          if (data) {
            try {
              const parsedData: PersistedUploadState[] = JSON.parse(data);
              // Filter out expired items
              const validItems = parsedData.filter(
                (item) => item.expiresAt > now,
              );

              if (validItems.length === 0) {
                // If all items expired, remove the entire entry
                localStorage.removeItem(key);
              } else if (validItems.length < parsedData.length) {
                // If some items expired, update the storage
                localStorage.setItem(key, JSON.stringify(validItems));
              }
            } catch (e) {
              console.error(`Error parsing data for key ${key}:`, e);
            }
          }
        });
      } catch (error) {
        console.error('Error cleaning up storage:', error);
      }
    };

    // Run cleanup on mount
    cleanupStorage();

    // Set up interval to run cleanup every hour
    const interval = setInterval(cleanupStorage, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Check if a file matches a paused upload
  const findMatchingPausedUpload = (
    file: File,
  ): PersistedUploadState | undefined => {
    // First try to match by name and size (most reliable)
    const matchByNameAndSize = pausedUploads.find(
      (upload) =>
        upload.fileName === file.name && upload.fileSize === file.size,
    );

    if (matchByNameAndSize) {
      return matchByNameAndSize;
    }

    // If no match, try to match just by name (less reliable)
    return pausedUploads.find((upload) => upload.fileName === file.name);
  };

  const handleFileSelect = async (
    files: {
      file: File;
      key: string;
      bucketName: string;
    }[],
    params: {
      uploadKey?: string;
      accountId: string;
    },
  ) => {
    if (!files.length) return;

    const selectedFiles = Array.from(files).filter((file) =>
      file.file.name.toLowerCase(),
    );
    const newFiles: UploadFile[] = [];
    const resumableFiles: UploadFile[] = [];

    // Process each selected file
    selectedFiles.forEach((file) => {
      // Check if this file matches a paused upload
      const matchingUpload = findMatchingPausedUpload(file.file);

      if (matchingUpload) {
        // This is a resumable upload
        console.log(
          `Found matching paused upload for ${file.file.name}. Resuming from chunk ${matchingUpload.currentChunk + 1} of ${matchingUpload.totalChunks}`,
        );

        // Create a resumable upload file with the real File object
        const resumableFile: UploadFile = {
          ...matchingUpload,
          file: file.file,
          status: 'paused',
          uploadSpeed: 0,
          lastUpdated: Date.now(),
          speedSamples: [], // Initialize empty array for speed samples
        };

        resumableFiles.push(resumableFile);

        // Remove the placeholder entry from files state
        setFiles((prev) => prev.filter((f) => f.id !== matchingUpload.id));
      } else {
        // This is a new upload
        const totalChunks = Math.ceil(file.file.size / CHUNK_SIZE);
        newFiles.push({
          id: `${file.file.name}-${Date.now()}`,
          file: file.file,
          fileName: file.file.name,
          fileSize: file.file.size,
          fileType: file.file.type,
          progress: 0,
          status: 'pending' as const,
          parts: [],
          currentChunk: 0,
          totalChunks,
          chunkProgress: 0,
          lastUpdated: Date.now(),
          uploadSpeed: 0,
          speedSamples: [], // Initialize empty array for speed samples
          accountId: params.accountId,
          key: file.key,
          bucketName: file.bucketName,
          uploadKey: params.uploadKey,
        });
      }
    });

    // Add new files to state
    if (newFiles.length > 0) {
      setFiles((prev) => [...prev, ...newFiles]);
    }

    // Add resumable files to state
    if (resumableFiles.length > 0) {
      setFiles((prev) => [...prev, ...resumableFiles]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // const results = await Promise.all(
    //   newFiles
    //     .filter((f) => f.status === 'pending' || f.status === 'paused')
    //     .map(uploadFile),
    // );

    // onComplete?.(results);
  };

  const uploadFile = async (
    fileData: UploadFile,
    onComplete?: (results: UploadFileResult) => void,
  ) => {
    try {
      const file = fileData.file;

      // Update status to uploading
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                status: 'uploading',
                lastUpdated: Date.now(),
                uploadSpeed: 0,
              }
            : f,
        ),
      );

      // If we have an uploadId, we're resuming an upload
      let uploadId = fileData.uploadId;
      let startPartNumber = 1;
      const uploadedParts = [...fileData.parts];

      // For resuming uploads, start from the next chunk
      if (uploadId && fileData.parts.length > 0) {
        // Resume from the next chunk after the last successfully uploaded one
        startPartNumber = fileData.parts.length + 1;

        console.log(
          `Resuming upload for ${fileData.fileName} from chunk ${startPartNumber} of ${fileData.totalChunks}`,
        );

        // Update progress based on already uploaded parts
        const resumeProgress = Math.round(
          ((startPartNumber - 1) / fileData.totalChunks) * 100,
        );
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? {
                  ...f,
                  progress: resumeProgress,
                  currentChunk: startPartNumber - 1,
                  lastUpdated: Date.now(),
                }
              : f,
          ),
        );
      } else {
        // Initiate new multipart upload
        const initRes = await fetch(`/api/upload/${fileData.accountId}`, {
          method: 'POST',
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            key: fileData.key,
            bucketName: fileData.bucketName,
          }),
          headers: { 'Content-Type': 'application/json' },
        });

        if (!initRes.ok) throw new Error('Failed to initiate upload');

        uploadId = await initRes.text();

        // Update file with uploadId
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? { ...f, uploadId, lastUpdated: Date.now() }
              : f,
          ),
        );
      }

      if (!uploadId) throw new Error('No upload ID available');

      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      // Upload remaining parts
      for (
        let partNumber = startPartNumber;
        partNumber <= totalParts;
        partNumber++
      ) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // Update current chunk info
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? {
                  ...f,
                  currentChunk: partNumber,
                  chunkProgress: 0,
                  lastUpdated: Date.now(),
                  uploadStartTime: Date.now(),
                  uploadSpeed: 0,
                }
              : f,
          ),
        );

        // Get presigned URL for part upload
        const urlRes = await fetch(
          `/api/upload/${fileData.accountId}?uploadId=${uploadId}&partNumber=${partNumber}&fileName=${encodeURIComponent(file.name)}&key=${fileData.key}&bucketName=${fileData.bucketName}`,
        );

        if (!urlRes.ok) throw new Error('Failed to get upload URL');

        const presignedUrl = await urlRes.text();

        // Upload part to S3 with progress tracking
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl, true);

        // Track upload speed
        let lastLoaded = 0;
        let lastTime = Date.now();

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const now = Date.now();
            const chunkProgress = Math.round(
              (event.loaded / event.total) * 100,
            );

            // Calculate upload speed
            const loadDiff = event.loaded - lastLoaded;
            const timeDiff = now - lastTime;

            if (timeDiff >= SPEED_SAMPLE_INTERVAL) {
              // Only update speed every second
              const speed = Math.round((loadDiff / timeDiff) * 1000); // bytes per second

              // Update chunk progress and speed
              setFiles((prev) =>
                prev.map((f) => {
                  if (f.id !== fileData.id) return f;

                  // Initialize uploadStartedAt if not set
                  const uploadStartedAt = f.uploadStartedAt || now;

                  // Calculate overall progress
                  const completedChunksProgress =
                    ((partNumber - 1) / totalParts) * 100;
                  const currentChunkContribution =
                    (chunkProgress / 100) * (1 / totalParts) * 100;
                  const totalProgress = Math.round(
                    completedChunksProgress + currentChunkContribution,
                  );

                  // Update speed samples
                  const newSpeedSamples = [...f.speedSamples, speed].slice(
                    -SPEED_SAMPLE_SIZE,
                  );
                  const averageSpeed = calculateMovingAverage(newSpeedSamples);

                  // Calculate more stable ETA based on overall progress and time elapsed
                  const totalBytes = file.size;
                  const uploadedBytes = (totalProgress / 100) * totalBytes;
                  const remainingBytes = totalBytes - uploadedBytes;

                  // Calculate ETA using both current speed and average progress rate
                  const timeElapsed = (now - uploadStartedAt) / 1000; // in seconds
                  const overallBytesPerSecond = uploadedBytes / timeElapsed;
                  const weightedSpeed =
                    averageSpeed * 0.7 + overallBytesPerSecond * 0.3;
                  const eta =
                    weightedSpeed > 0 ? remainingBytes / weightedSpeed : 0;

                  return {
                    ...f,
                    chunkProgress,
                    progress: totalProgress,
                    lastUpdated: now,
                    uploadSpeed: averageSpeed,
                    speedSamples: newSpeedSamples,
                    eta,
                    uploadStartedAt,
                  };
                }),
              );

              // Update tracking variables
              lastLoaded = event.loaded;
              lastTime = now;
            }
          }
        };

        // Create a promise to handle the XHR completion
        await new Promise<void>((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`HTTP Error: ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network Error'));
          xhr.send(chunk);
        });

        const eTag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') || '';
        uploadedParts.push({ PartNumber: partNumber, ETag: eTag });

        // Update progress to show chunk is complete
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? {
                  ...f,
                  chunkProgress: 100,
                  parts: [...uploadedParts],
                  lastUpdated: Date.now(),
                }
              : f,
          ),
        );

        // Save progress to localStorage after each chunk
        // This ensures we can resume even if the browser crashes right after a chunk completes
        const expiresAt = Date.now() + STORAGE_EXPIRY_HOURS * 60 * 60 * 1000;
        const persistedState: PersistedUploadState = {
          id: fileData.id,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          fileType: fileData.fileType,
          progress: Math.round((partNumber / totalParts) * 100),
          status: 'paused', // Mark as paused so it can be resumed
          uploadId,
          parts: [...uploadedParts],
          currentChunk: partNumber,
          totalChunks: totalParts,
          chunkProgress: 100,
          lastUpdated: Date.now(),
          uploadSpeed: 0,
          resumable: true,
          expiresAt,
          speedSamples: [],
          uploadStartedAt: Date.now(),
          accountId: fileData.accountId,
          key: fileData.key,
          bucketName: fileData.bucketName,
          uploadKey: fileData.uploadKey,
        };

        // Save just this file's state to localStorage
        try {
          const savedUploads = localStorage.getItem(STORAGE_KEY);
          let allUploads: PersistedUploadState[] = [];

          if (savedUploads) {
            allUploads = JSON.parse(savedUploads);
            // Replace or add this upload
            const existingIndex = allUploads.findIndex(
              (u) => u.id === fileData.id,
            );
            if (existingIndex >= 0) {
              allUploads[existingIndex] = persistedState;
            } else {
              allUploads.push(persistedState);
            }
          } else {
            allUploads = [persistedState];
          }

          localStorage.setItem(STORAGE_KEY, JSON.stringify(allUploads));
        } catch (error) {
          console.error('Error saving upload state:', error);
        }
      }

      // Complete multipart upload
      const completeRes = await fetch(`/api/upload/${fileData.accountId}`, {
        method: 'PUT',
        body: JSON.stringify({
          uploadId,
          fileName: file.name,
          parts: uploadedParts,
          size: file.size,
          key: fileData.key,
          bucketName: fileData.bucketName,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!completeRes.ok) throw new Error('Failed to complete upload');

      // Mark as completed
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                status: 'completed',
                progress: 100,
                lastUpdated: Date.now(),
                uploadSpeed: 0,
              }
            : f,
        ),
      );

      console.log(
        `Upload complete: ${file.name} has been uploaded successfully.`,
      );

      // Remove completed upload from localStorage
      try {
        const savedUploads = localStorage.getItem(STORAGE_KEY);
        if (savedUploads) {
          const allUploads: PersistedUploadState[] = JSON.parse(savedUploads);
          const filteredUploads = allUploads.filter(
            (u) => u.id !== fileData.id,
          );
          localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredUploads));

          removeFile(fileData.id);

          onComplete?.({
            id: fileData.id,
            name: fileData.fileName,
            size: fileData.fileSize,
            key: fileData.key,
            bucketName: fileData.bucketName,
          });

          return {
            id: fileData.id,
            name: fileData.fileName,
            size: fileData.fileSize,
            key: fileData.key,
            bucketName: fileData.bucketName,
          };
        }
      } catch (error) {
        console.error('Error removing completed upload from storage:', error);
        throw error;
      }
      throw new Error('Failed to remove completed upload from storage');
    } catch (error) {
      console.error('Upload error:', error);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                status: 'error',
                error: error instanceof Error ? error.message : 'Upload failed',
                lastUpdated: Date.now(),
                uploadSpeed: 0,
              }
            : f,
        ),
      );

      console.error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );

      throw error;
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setPausedUploads((prev) => prev.filter((f) => f.id !== id));

    // Remove from localStorage
    console.log(`Removing file ${id} from localStorage`);
    try {
      const savedUploads = localStorage.getItem(STORAGE_KEY);
      if (savedUploads) {
        console.log(`Found ${savedUploads.length} uploads in localStorage`);
        const allUploads: PersistedUploadState[] = JSON.parse(savedUploads);
        console.log(`All uploads: ${allUploads.length}`);
        const filteredUploads = allUploads.filter((u) => u.id !== id);
        console.log(`Filtered uploads: ${filteredUploads.length}`);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredUploads));
      }
    } catch (error) {
      console.error('Error removing upload from storage:', error);
    }
  };

  // Function to handle file re-selection for paused uploads
  const handleResumeFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    pausedFileId: string,
    onComplete?: (results: UploadFileResult) => void,
  ) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    if (!file) return;

    const pausedFile = files.find((f) => f.id === pausedFileId);
    if (!pausedFile) return;

    // Verify file matches the paused upload
    if (
      file.name !== pausedFile.fileName ||
      file.size !== pausedFile.fileSize
    ) {
      // Show error message in the UI but keep status as paused to allow retry
      setFiles((prev) =>
        prev.map((f) =>
          f.id === pausedFileId
            ? {
                ...f,
                error:
                  "Selected file doesn't match the paused upload. Please select the original file.",
                lastUpdated: Date.now(),
              }
            : f,
        ),
      );

      console.error(
        "File mismatch: The selected file doesn't match the paused upload.",
      );
      return;
    }

    // Create updated file data with the real file
    const updatedFileData = {
      ...pausedFile,
      file,
      status: 'paused' as const,
      error: undefined, // Clear any previous error
    };

    // Update the file object in the paused upload
    setFiles((prev) =>
      prev.map((f) => (f.id === pausedFileId ? updatedFileData : f)),
    );

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Start the upload immediately with the updated file data
    // This avoids the race condition with the state update
    setTimeout(() => {
      uploadFile(updatedFileData, onComplete);
    }, 50);
  };

  const resumeUpload = async (
    fileData: UploadFile,
    onComplete?: (results: UploadFileResult) => void,
  ) => {
    // If we have a real file object, resume upload directly
    let results: UploadFileResult | null = null;

    if (fileData.file.size > 0) {
      // Clear any previous error
      if (fileData.error) {
        const updatedFileData = { ...fileData, error: undefined };
        setFiles((prev) =>
          prev.map((f) => (f.id === fileData.id ? updatedFileData : f)),
        );

        // Use the updated data directly
        results = await uploadFile(updatedFileData, onComplete);
      } else {
        results = await uploadFile(fileData, onComplete);
      }

      return results;
    }

    // Otherwise, we need to prompt the user to select the file again
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = (e) => {
      if (e.target instanceof HTMLInputElement) {
        handleResumeFileSelect(
          e as unknown as React.ChangeEvent<HTMLInputElement>,
          fileData.id,
          onComplete,
        );
      }
    };

    // Show a message to the user
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileData.id
          ? {
              ...f,
              error: 'Please select the original file to resume upload',
              status: 'paused',
            }
          : f,
      ),
    );

    fileInput.click();
  };

  const uploadAllPending = (
    onComplete?: (results: UploadFileResult) => void,
  ) => {
    files
      .filter((f) => f.status === 'pending' || f.status === 'paused')
      .forEach((file) => uploadFile(file, onComplete));
  };

  const startUpload = async ({
    filesToUpload,
    onComplete,
  }: {
    filesToUpload: UploadFile[];
    onComplete?: (results: UploadFileResult) => void;
  }) => {
    await Promise.all(
      filesToUpload.map((file) => uploadFile(file, onComplete)),
    );
  };

  const value = {
    files,
    pausedUploads,
    fileInputRef,
    uploadAllPending,
    startUpload,
    handleFileSelect,
    uploadFile,
    removeFile,
    resumeUpload,
    isOpen,
    setIsOpen,
  };

  return (
    <FileUploaderContext.Provider value={value}>
      {children}
    </FileUploaderContext.Provider>
  );
};

export function useUploadKeyFiles(uploadKey?: string) {
  const { files, startUpload } = useFileUploader();

  const filteredFiles = uploadKey
    ? files.filter((file) => file.uploadKey === uploadKey)
    : files;

  return {
    files: filteredFiles,
    startUpload: ({
      onComplete,
    }: {
      onComplete?: (results: UploadFileResult) => void;
    }) => startUpload({ filesToUpload: filteredFiles, onComplete }),
  };
}
